'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import { KiaoModal } from '@kiao-voice/ui-kit'
import { AuthFlash } from './auth-flash'
import { signOut as defaultSignOut } from '../batch-transcription/auth-storage'

/** Logout flash dwell before redirecting home (mirrors the login flash). */
const LOGOUT_FLASH_DELAY_MS = 1200

interface LogoutFlashProps {
  /** Test seam — defaults to the real sign-out (server revoke + token clear). */
  signOutFn?: () => Promise<void>
  /** Test seam — defaults to next/navigation `router.push`. */
  navigate?: (path: string) => void
}

/**
 * Instant, non-destructive sign-out flash — the shared exit half of the
 * login/logout flash pair. Mounting it IS the sign-out: it fires the revoke
 * once (no confirm dialog), shows the centred `<AuthFlash icon="exit">` in a
 * head-collapsed modal, then sends the user home when the dwell elapses.
 *
 * Render it conditionally (`{signingOut && <LogoutFlash … />}`); it tears
 * itself down when the home navigation unmounts the host. Reused by every
 * 登出 entry point so the flash is never reimplemented per shell.
 */
export function LogoutFlash({ signOutFn = defaultSignOut, navigate }: LogoutFlashProps) {
  const { t } = useTranslation('web')
  const router = useRouter()
  const fired = useRef(false)

  useEffect(() => {
    // Run exactly once even under StrictMode double-invoke.
    if (fired.current) return
    fired.current = true
    // `signOutFn` (the real `signOut()`, by default) owns the WHOLE sign-out
    // sequence and clears the local token synchronously before its first
    // `await` — the user is really signed out before the dwell-timed redirect
    // home, independent of whether the server-side revoke succeeds, hangs, or
    // is offline. Do NOT clear storage here as well: `signOut()` must capture
    // the token first to send the server-side revoke with a valid bearer (see
    // its docblock).
    void signOutFn().catch((err) => {
      reportHandledException(err, { source: 'features/auth/logout-flash.tsx' })
      // Deliberate report-and-continue (NOT a silent catch): the visible
      // recovery is that the LOCAL sign-out already completed synchronously
      // above — the user is signed out on this device regardless, which is
      // the correct outcome even fully offline. What a failure here leaves
      // behind is only the server-side token, which expires by TTL; blocking
      // or failing the logout UX over the revoke round-trip would be worse.
      console.warn(
        '[logout-flash] server-side sign-out revoke failed; local session already cleared:',
        err
      )
    })
  }, [signOutFn])

  const goHome = () => {
    if (navigate) navigate('/')
    else router.push('/')
  }

  return (
    <KiaoModal
      open
      onClose={goHome}
      title={t('auth.doneSignedOut')}
      subtitle={t('auth.doneRedirectingHome')}
      closeLabel={t('auth.close')}
      // The head is always collapsed here (logout has no email step), so the
      // dialog is named explicitly — its visible <h3> carries the same copy.
      ariaLabel={t('auth.doneSignedOut')}
      className="kiao-login is-done"
    >
      <AuthFlash
        icon="exit"
        title={t('auth.doneSignedOut')}
        subtitle={t('auth.doneRedirectingHome')}
        delayMs={LOGOUT_FLASH_DELAY_MS}
        redirectTo="/"
        navigate={navigate}
      />
    </KiaoModal>
  )
}
